/**
 * The `nav-models` and `nav-properties` rails: the two principal navigation objects.
 *
 * Corrections 2 and 6 are this module's reason to exist. `Models: 3 row(s) / Id / Label / Kind /
 * Detail` was a debug representation of a navigation surface; each model's NAME over its PURPOSE
 * QUESTION is the human one, and activating it draws that model. The properties rail is the place
 * the author asked for MORE rather than less: a mark and a status word beside every tracked claim,
 * with the claim's full reading one disclosure down, and each claim linking to a model it derives
 * from.
 *
 * **The §9a decision, recorded because the design asked whoever took this wave to make it.** The
 * view-model-to-DOM binder wrote all four readouts including the property list, and wave 3 owns
 * that file; a rail cannot change how a property row reads without either growing a renderer here
 * or editing the binder. BOTH, in one direction:
 *
 *   - the rail's ROW is a new rendering — a mark, a word, a claim, a disclosure — not a restyled
 *     `propertyBlock`, so there was nothing to lift out of the binder and the renderer is here;
 *   - the binder gave up the one root it no longer writes. Leaving it writing `#question-list`
 *     would make two authors race on paint order, and which one won would be a fact about the
 *     order of two statements in the composition root rather than a decision anybody made.
 *
 * A removal is also the direction wave 3's work in that file already runs (it deletes the dead form
 * code); a SPLIT — four readouts into four regions — is what wave 0 refused and what would have
 * collided. The full reading is still rendered by `propertyBlock`, imported from the binder, so one
 * module still decides how a property reads in full and the rail decides only what is visible first.
 *
 * **Nothing here is a command, so nothing here is a button** — the rule `shell/inspector.ts` set
 * for the same reason. Every control in the rails navigates: a model link draws that model, a claim
 * link moves the workspace to a model the claim derives from, and the two `+` rows go to the
 * control that performs the operation, resolved from the capability registry's own declaration of
 * where that control is. A rail that minted its own Add button would be a control the registry does
 * not declare, which the browser tier reads as an unregistered site — correctly. The `+ Add` MENUS
 * correction 4 sketches are the editing wave's, and they will re-site those affordances rather than
 * add new ones.
 *
 * **The member sets are derived, never enumerated.** The models rail walks `vm.subjects`, which is
 * the one list of drawable purposeful models — one per model AND one per machine, because a machine
 * carries its own purpose and a rail built from `system.models` alone would report worker-queue as
 * having one model and be wrong. The properties rail walks `vm.properties`. Neither list is
 * reconstructed here, so neither can disagree with the surface that already has it.
 */
import type { CapabilityId } from "../../app/capabilities.ts";
import { CAPABILITIES } from "../../app/capabilities.ts";
import { statusMark } from "../../app/properties.ts";
import type { CompositionEdge } from "../../app/services.ts";
import type { CanonicalSystem } from "../../ir/types.ts";
import { propertyBlock } from "../render-dom.ts";
import { witnessFocus } from "./workspace.ts";
import { resolveSubject, subjectValue } from "../view-model.ts";
import type { PropertyRow, ViewModel } from "../view-model.ts";
import { byId, mountIf } from "./context.ts";
import type { ShellContext, ShellFrame, ShellRegion } from "./context.ts";
import { regionHost, surfaceElement } from "./surfaces.ts";

// --------------------------------------------------------------------------------------------
// The reading
// --------------------------------------------------------------------------------------------

/** One model in the rail: what it is called, what it asks, and whether it is the one being drawn. */
export interface ModelRailRow {
  /** The `kind:id` subject value — what activating the row assigns to `ViewState.target`. */
  readonly subject: string;
  readonly name: string;
  /**
   * The model's stored "Model in words", or null when its author wrote none. Author ruling
   * (261006): the purpose question left the default surface with the rest of the purpose block;
   * the description is the single authored prose the rail may show, and absent means absent —
   * no sentence is synthesized in its place.
   */
  readonly words: string | null;
  readonly current: boolean;
}

/**
 * The three-valued mark the author drew as `✓ / ✗ / ?`.
 *
 * A GLYPH and a WORD together. The glyph is the three-valued summary a sighted reader takes in at a
 * glance; the word is the six-valued truth, because `?` over four different statuses would say less
 * than the status does and the house rule refuses a glyph carrying a status alone.
 */
export interface RailMark {
  readonly glyph: string;
  readonly word: string;
}

/** One tracked claim in the rail. */
export interface PropertyRailRow {
  readonly id: string;
  readonly mark: RailMark;
  readonly claim: string;
  /** "requirement" or "property" — §13's distinction, as the word it already is. */
  readonly kind: string;
  /**
   * Where activating the claim takes the workspace, or null when the claim grounds in nothing.
   *
   * Null is a fact, not a gap: a status with no grounding is UX-I5's honest-empty case, and a link
   * to nowhere would be the unverifiable navigation §2.1 refuses.
   */
  readonly explains: string | null;
  /**
   * The claim's id when it is CROSS-MODEL, so activating it opens the COMPOSED workspace view
   * instead of drawing one ground. A claim no single model states must not navigate to a single
   * model — that is the reading the composed view exists to correct.
   */
  readonly composes: string | null;
  /** The full reading, disclosed. The same row the binder renders in full. */
  readonly full: PropertyRow;
}

/**
 * One place two models' meanings meet, as the rail reads it: the two subjects, the shared
 * element, and the registered binding — `Worker Pool — job — Job Lifecycle, bound by:
 * machine-of-entity`. Activating it composes the two subjects in the workspace with that
 * binding's reading open.
 */
export interface MeetingRow {
  /** `<element>|<binding>` — the click handler opens the element's composed view, that binding's
   * reading first. */
  readonly value: string;
  readonly text: string;
  /** §23.2's kind line, from the registry's own name. */
  readonly label: string;
}

export interface NavRails {
  readonly models: readonly ModelRailRow[];
  /** What to say instead, when there is nothing to list. Null when the list is non-empty. */
  readonly modelsEmpty: string | null;
  /**
   * The model-composition topology under the model list — where the reductions' meanings meet,
   * derived from the binding registry. Empty for a system whose models share nothing, and then
   * nothing is rendered: a one-model system's rail stays a list.
   */
  readonly meetings: readonly MeetingRow[];
  readonly properties: readonly PropertyRailRow[];
  readonly propertiesEmpty: string | null;
}

/**
 * The glyph and word tables moved to `src/app/properties.ts` (`STATUS_GLYPH` / `statusWord` /
 * `statusMark`), beside `STATUS_TEXT`, when the workspace began painting the same marks as claim
 * sub-lines inside node boxes — one table, two surfaces, no drift. Re-exported so the rail's
 * callers keep their import.
 */
export { statusWord } from "../../app/properties.ts";

/**
 * A stale verdict is marked `?`, whatever the verdict was.
 *
 * The verdict describes a revision the system has moved past, so neither `✓` nor `✗` would be a
 * claim about what is loaded. `PropertyRow` demotes the verdict out of `status` for the same reason;
 * the mark follows it rather than deciding separately.
 */
const markOf = (row: PropertyRow): RailMark => statusMark(row.statusKey, row.stale);

/**
 * Both rails, as data, before any element exists.
 *
 * Takes the view model and the system rather than reaching for services: every projection paints
 * from one observation (UX-I3), and a pure function of the frame is how that is held here as well
 * as asserted.
 */
export function navRails(
  vm: ViewModel, system: CanonicalSystem, target: string | null,
  topology: readonly CompositionEdge[] = [],
): NavRails {
  const principal = resolveSubject(system, target);
  const current = principal === null ? null : subjectValue(principal);

  const models: ModelRailRow[] = vm.subjects.flatMap((choice) => {
    const subject = resolveSubject(system, choice.value);
    // `subjects` is built from the system's own models and machines, so this resolves for every
    // member. Dropping an unresolvable one rather than falling back: `resolveSubject` answers a
    // missing target with the FIRST model, which is right for a navigation default and would be a
    // duplicate row here.
    if (subject === null || subjectValue(subject) !== choice.value) return [];
    const purpose = subject.kind === "model"
      ? system.models.get(subject.id)?.purpose
      : system.machines.get(subject.id)?.purpose;
    if (purpose === undefined) return [];
    return [{
      subject: choice.value,
      name: subject.kind === "model"
        ? system.models.get(subject.id)?.label ?? subject.id
        : system.machines.get(subject.id)?.label ?? subject.id,
      words: (subject.kind === "model"
        ? system.models.get(subject.id)?.description
        : system.machines.get(subject.id)?.description) ?? null,
      current: choice.value === current,
    }];
  });

  const properties: PropertyRailRow[] = vm.properties.map((row) => ({
    id: row.id,
    mark: markOf(row),
    claim: row.statement,
    kind: row.kind,
    explains: row.groundSubjects[0] ?? null,
    composes: row.composes,
    full: row,
  }));

  const subjectLabel = (s: { kind: string; id: string }): string =>
    s.kind === "model"
      ? system.models.get(s.id)?.label ?? s.id
      : system.machines.get(s.id)?.label ?? s.id;
  const meetings: MeetingRow[] = topology.map((edge) => ({
    value: `${edge.element}|${edge.binding.name}`,
    text: `${subjectLabel(edge.a)} — ${edge.element} — ${subjectLabel(edge.b)}`,
    label: `bound by: ${edge.binding.name}`,
  }));

  return {
    models,
    meetings,
    modelsEmpty: models.length > 0
      ? null
      : "No purposeful model yet. Add one, and state the engineering question it answers.",
    properties,
    propertiesEmpty: properties.length > 0
      ? null
      // The taught loop, named where the user is standing: the human tells the AGENT, which
      // formalizes the claim; the workbench shows what was formalized and whether it holds. The
      // old sentence sent the reader to the ask bar, which the viewer surface no longer offers —
      // the browser is the inspection surface, not the agent surface.
      //
      // What a property IS — a saved question, re-evaluated on every later revision — stays in
      // Learn (`src/learn/workbench-guide.ts`, "What a property is"). A rail row is where a
      // reader learns the next act, not the semantics of the object it produces.
      : "This model system asserts no claims yet. Tell your agent what must hold of the system; "
        + "it records the claim, and the verdict appears here.",
  };
}

// --------------------------------------------------------------------------------------------
// Where a `+` row goes
// --------------------------------------------------------------------------------------------

/**
 * The element that performs a capability, read out of the registry that declares it.
 *
 * The rails offer `+ Model` and `+ Property`, and the operations behind them already have declared
 * human sites. Linking to the registry's own declaration rather than to an id typed here means the
 * rail follows the control when a later wave re-sites it, and that a rail row can never point at a
 * control the registry does not know about — the two failures a hand-typed `href="#add-model-go"`
 * would invite one after the other.
 *
 * Resolved at PAINT, and the first site that is present and enabled wins. A capability can declare
 * more than one site (saving a property is reachable from the ask bar's Track and from the standalone
 * save form), and which of them a user can actually use right now is a fact about the live page.
 */
function operationHref(capability: CapabilityId): string | null {
  const declared = CAPABILITIES.find((c) => c.id === capability);
  if (declared === undefined) return null;
  for (const affordance of declared.human) {
    if (affordance.status === "absent") continue;
    const node = document.getElementById(affordance.element.id);
    if (node === null) continue;
    if (node instanceof HTMLButtonElement && node.disabled) continue;
    return affordance.element.id;
  }
  return null;
}

// --------------------------------------------------------------------------------------------
// The DOM
// --------------------------------------------------------------------------------------------

const el = <K extends keyof HTMLElementTagNameMap>(
  tag: K, text?: string, className?: string,
): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);
  if (text !== undefined) node.textContent = text;
  if (className !== undefined) node.className = className;
  return node;
};

/**
 * The two things a rail link does, and the surface each lands on.
 *
 * `draw` moves the drawn subject; `explain` does the same thing for the model a claim derives from,
 * and is a separate action only so the announcement can say which of the two just happened.
 * `invoke` goes to a control rather than a region, so it carries its own destination id.
 */
type RailAction = "draw" | "explain" | "invoke" | "compose" | "binding";

/** Resolved through the table, so the workspace's element id is not written here as well. */
function workspaceHref(): string {
  const id = surfaceElement("workspace");
  if (id === null) throw new Error("the rails link to surface 'workspace', which SURFACES declares planned");
  return `#${id}`;
}

function railLink(action: RailAction, arg: string, href: string): HTMLAnchorElement {
  const link = el("a");
  link.href = href;
  link.dataset["rail"] = action;
  link.dataset["arg"] = arg;
  return link;
}

function modelNode(row: ModelRailRow): HTMLLIElement {
  const li = el("li");
  const link = railLink("draw", row.subject, workspaceHref());
  link.append(el("strong", row.name));
  if (row.current) {
    // `aria-current` is the announced half and a chip is the seen half. Neither alone: the attribute
    // is invisible without a stylesheet this module does not own, and a styled border is invisible
    // to a screen reader.
    link.setAttribute("aria-current", "true");
    link.append(document.createTextNode(" "), el("span", "being viewed", "state"));
  }
  // Model in words, under the name and inside the link — the stored authored description, when one
  // exists. A rail of bare names teaches a reader nothing about why there is more than one model,
  // and the description is the one authored prose the default surface shows (author ruling,
  // 261006; the purpose question left the surface with the rest of the purpose block).
  if (row.words !== null) link.append(el("p", row.words, "purpose"));
  li.append(link);
  return li;
}

function propertyNode(row: PropertyRailRow): HTMLLIElement {
  const li = el("li");
  // The claim's id, on the row that shows it. Not decoration: the convergence gate counts the rows
  // the human surface renders against `savedQueries()`, and it used to count every direct child of
  // the host because every direct child WAS a property. A rail has a `+ Property` row and a list
  // wrapper too, so the set needs a name rather than a position.
  li.dataset["property"] = row.id;
  const head = el("p");
  // The glyph is hidden from the accessibility tree. A screen reader announces "✓" as "check mark"
  // or as nothing at all depending on the pairing, and the word beside it already carries the
  // status — so the glyph is decoration for the eye and is marked as such.
  const glyph = el("span", row.mark.glyph, "mark");
  glyph.setAttribute("aria-hidden", "true");
  head.append(glyph, document.createTextNode(" "), el("span", row.mark.word, "state"));
  head.append(document.createTextNode(" "), el("span", row.kind, "state"), document.createTextNode(" "));

  if (row.composes !== null) {
    // A cross-model claim composes: no single model states it, so the link opens the composed
    // workspace view — the models its vocabulary spans, the binding between them, and the
    // constraint — instead of navigating to one ground as though one ground could explain it.
    const link = railLink("compose", row.composes, workspaceHref());
    link.textContent = row.claim;
    head.append(link, document.createTextNode(" "), el("span", "crosses models", "state"));
  } else if (row.explains === null) {
    head.append(el("span", row.claim));
  } else {
    const link = railLink("explain", row.explains, workspaceHref());
    link.textContent = row.claim;
    head.append(link);
  }
  li.append(head);

  // SH-I2: a real disclosure control, in the tab order, announcing its own expanded state, so a
  // keyboard user and a sighted user open the same thing by the same act. UX-I8's other half is
  // here too — every field the flat list showed at once is still reachable, and the summary says
  // what is behind it rather than leaving a reader to guess whether anything is.
  const details = el("details");
  details.append(
    el("summary", "Status, grounding and evidence"),
    propertyBlock(row.full),
  );
  // "Show on model" — the claim's evidence drawn ON the diagram, from the rail itself. The same
  // view movement the ask surface's witness button performs (`ViewState.witness`, outside semantic
  // state), offered where the property LIVES so the viewer surface explains verdicts without any
  // authoring affordance. A claim with nothing drawable gets the honest sentence when activated —
  // which cases those are is `witnessFocus`'s contract, resolved at click against the live system.
  const show = el("button", "Show on model");
  show.type = "button";
  show.dataset["rail"] = "witness";
  show.dataset["arg"] = row.id;
  const showRow = el("p");
  showRow.append(show);
  details.append(showRow);
  li.append(details);
  return li;
}

/**
 * A `+` row: the operation's name, linking to the control that performs it.
 *
 * When the registry's sites are all absent from the page the row is TEXT saying so rather than a
 * link to nothing. That is the `purposeBlock` precedent — silence and "nothing is declared here"
 * look identical on screen and only one of them is a fact.
 */
function addNode(label: string, capability: CapabilityId, missing: string): HTMLLIElement {
  const li = el("li", undefined, "rail-add");
  const id = operationHref(capability);
  if (id === null) {
    li.append(el("span", missing, "caveat"));
    return li;
  }
  const link = railLink("invoke", id, `#${id}`);
  link.textContent = label;
  li.append(link);
  return li;
}

export function mountNav(ctx: ShellContext): ShellRegion {
  const rail = byId("nav");
  const models = regionHost("nav-models");
  const properties = regionHost("nav-properties");

  /**
   * ONE listener per rail, bound once to markup the page ships, reading the link it was given.
   *
   * Delegation for the reason `shell/inspector.ts` gives: the rows are rebuilt whenever the reading
   * changes, and a handler attached to a rebuilt element is a handler re-bound on every paint. The
   * link carries its own argument, so nothing closes over the frame it was built in.
   *
   * The default is not prevented for a navigation: the href is a real in-page destination, so the
   * browser moves the reader to the region that is about to change while the handler changes what
   * it shows. For `invoke` the default IS prevented, because a fragment jump scrolls a control into
   * view without focusing it — and an affordance you have to find again after arriving is the
   * reachability §2.2 declines to call reachable.
   */
  const navigate = (host: HTMLElement): void => {
    host.addEventListener("click", (event) => {
      const from = event.target;
      if (!(from instanceof Element)) return;
      const link = from.closest<HTMLElement>("[data-rail]");
      if (link === null) return;
      const arg = link.dataset["arg"];
      if (arg === undefined) return;
      const action = link.dataset["rail"];
      if (action === "witness") {
        // Draw this claim's evidence over the model or machine that carries it. Re-derived at
        // click against the live system — a retracted claim or a now-refusing statement answers
        // honestly instead of drawing a stale picture.
        const system = ctx.workspace.state.system;
        const focus = witnessFocus(system, arg, (raw) => ctx.workspace.query(raw));
        if (focus === null) {
          ctx.announce("This claim has no drawable evidence right now: a refusal or a conclusive "
            + "absence has no path or trace to draw, and an unevaluated claim has none yet. The "
            + "status, grounding and any refusal sentence in this row are the whole of the "
            + "evidence.");
          return;
        }
        ctx.viewState.target = subjectValue(focus.subject);
        ctx.viewState.composed = null;
        ctx.viewState.witness = arg;
        ctx.announce(`Workspace draws the ${focus.result.evidence?.role ?? "evidence"} for this `
          + `claim over ${focus.subject.id}, with its steps numbered on the diagram and listed in `
          + "the model reading.");
        ctx.repaint();
        return;
      }
      if (action === "invoke") {
        event.preventDefault();
        const control = document.getElementById(arg);
        if (control === null) return;
        control.focus();
        ctx.announce(`Focus moved to ${control.textContent?.trim() ?? arg}.`);
        return;
      }
      if (action === "compose") {
        // The composed view for a cross-model claim (§23). The one-model target is untouched, so
        // leaving the composition returns to whatever was being drawn.
        ctx.viewState.composed = { kind: "property", id: arg };
        ctx.announce("Workspace shows the composed view for this claim: the models it spans, the "
          + "binding between them, and the constraint being checked.");
        ctx.repaint();
        return;
      }
      if (action === "binding") {
        const [element, name] = arg.split("|");
        if (element === undefined || name === undefined) return;
        ctx.viewState.composed = { kind: "element", id: element, focus: name };
        ctx.announce(`Workspace shows the composed view bound on ${element}, with the `
          + `${name} binding's reading open.`);
        ctx.repaint();
        return;
      }
      ctx.viewState.target = arg;
      ctx.viewState.composed = null;
      ctx.announce(action === "explain"
        ? `Workspace now shows a model this claim derives from: ${arg}.`
        : `Workspace now draws ${link.querySelector("strong")?.textContent ?? arg}.`);
      ctx.repaint();
    });
  };
  navigate(models);
  navigate(properties);

  /**
   * The reading last rendered, as data.
   *
   * Rebuilt only when the READING changes, which is what keeps a disclosure the user opened open
   * across the repaints its neighbours cause — the inspector's argument, and it bites harder here
   * because every property row owns a `<details>`. The signature covers the `+` rows' resolved
   * destinations too, since those depend on the live page rather than on the frame.
   */
  let rendered: string | null = null;

  return {
    paint: (frame: ShellFrame) => {
      // The rail navigates a loaded system. With nothing loaded, Start occupies the page and two
      // empty rails beside it are furniture — SH-I1's reasoning applied to the surfaces that have
      // nothing to navigate.
      mountIf(rail, frame.state.loaded);
      if (!frame.state.loaded) { rendered = null; return; }

      const reading = navRails(
        frame.vm, frame.state.system, ctx.viewState.target, ctx.workspace.modelCompositionGraph());
      const addModel = operationHref("create-model");
      const addProperty = operationHref("save-property");
      const signature = JSON.stringify([reading, addModel, addProperty]);
      if (signature === rendered) return;
      rendered = signature;

      const modelList = el("ul", undefined, "rail");
      if (reading.modelsEmpty !== null) modelList.append(el("li", reading.modelsEmpty, "intro"));
      for (const row of reading.models) modelList.append(modelNode(row));
      modelList.append(addNode(
        "+ Model", "create-model",
        "Adding a model has no control on this page right now.",
      ));
      models.replaceChildren(modelList);

      // The topology reading, under the list: the rail as a flat list says the models are peers,
      // and for a system whose meanings meet that reading is wrong. One row per registered
      // meeting — subject, shared element, subject — activating the correspondence in the
      // workspace. Derived from the binding registry; absent entirely when nothing meets.
      if (reading.meetings.length > 0) {
        models.append(el("p", "Where models meet", "sublabel"));
        const meetingList = el("ul", undefined, "rail");
        for (const row of reading.meetings) {
          const li = el("li");
          const link = railLink("binding", row.value, workspaceHref());
          link.append(el("strong", row.text));
          link.append(el("p", row.label, "purpose"));
          li.append(link);
          meetingList.append(li);
        }
        models.append(meetingList);
      }

      const propertyList = el("ul", undefined, "rail");
      if (reading.propertiesEmpty !== null) {
        propertyList.append(el("li", reading.propertiesEmpty, "intro"));
      }
      for (const row of reading.properties) propertyList.append(propertyNode(row));
      propertyList.append(addNode(
        "+ Property", "save-property",
        "Tracking a claim has no control on this page right now.",
      ));
      properties.replaceChildren(propertyList);
    },
  };
}
